/* The railroad diagram: a tree from `stackwalk.js` in, one SVG string out.
 *
 * **This is the reason anybody would use Regexray rather than the console they
 * already have open.** Free regex testers are five deep; a picture of the
 * pattern is what they do not have, and it is what makes a regular expression
 * somebody else wrote readable rather than decipherable.
 *
 * **PURE, AND THAT IS DELIBERATE.** Nothing here touches the DOM: the layout is
 * arithmetic and the output is a string. So the geometry can be tested under
 * node without a browser, which is the same split `highlight.js` made and for
 * the same reason - this repo cannot drive a browser in its suite, and the edge
 * cases are all in the arithmetic.
 *
 * **Text is measured rather than asked about.** The diagram is drawn in a
 * monospace face at a fixed size, so a character is a known width and the
 * layout is a pure function of the tree. Asking the browser to measure would
 * make the output depend on which fonts the reader happens to have, and would
 * put a layout pass in the middle of every keystroke.
 */

import { classifyOpen } from "./stackwalk.js";

// The metrics. One place, because every one of them appears in both the layout
// and the SVG, and a value that disagrees between the two is a diagram whose
// boxes do not contain their own text.
const CHAR = 7.2;          // width of one character at 12px in the mono stack
const TEXT = 12;           // font size
const PAD_X = 8;           // inside a terminal box, left and right
const PAD_Y = 6;           // inside a terminal box, top and bottom
const GAP = 18;            // between items in a sequence
const BRANCH_GAP = 10;     // between the branches of an alternation
const FAN = 16;            // horizontal room the branch fan needs on each side
const GROUP_PAD = 12;      // inside a group frame
const GROUP_LABEL = 14;    // room above a group's contents for its label
const LOOP = 14;           // vertical room a quantifier's loop needs below
const RADIUS = 6;          // corner radius on the rails
const LABEL_SIZE = 10;     // font size of a group's label

/* **THE LABEL IS DRAWN IN THE MONOSPACE FACE, AND THAT IS A CORRECTNESS
   DECISION RATHER THAN A TYPOGRAPHIC ONE.** A group's frame is sized to fit the
   wider of its contents and its label, so the label's width has to be KNOWN. In
   a proportional face it is not: it depends on which font the reader's browser
   picked, and the constant here would be a guess calibrated against whichever
   one happened to render while it was written. It was, at 5.6, and a longer
   label still overflowed its frame on a page whose face was wider than the one
   it was guessed from.

   In the mono face a character is exactly 0.6em, so the frame is right by
   construction in every browser and the measurement is testable. The label
   keeps its smaller size and lighter colour to stay clearly prose ABOUT the
   pattern rather than part of it. */
const LABEL_CHAR = LABEL_SIZE * 0.6;

const textWidth = (text) => Math.max(1, text.length) * CHAR;

/* --------------------------------------------------------------- measuring */

/**
 * Give every node a width, a height, and a RAIL: the y offset, within its own
 * box, of the line that enters on the left and leaves on the right.
 *
 * The rail is what makes a sequence line up. Two boxes of different heights sit
 * side by side with their rails at the same absolute y, so the connecting line
 * is straight - which is the difference between a diagram that reads as one
 * track and a diagram that reads as a staircase.
 */
export function measure(node) {
  if (node.kind === "atom" || node.kind === "inert") {
    const label = display(node);
    node.w = textWidth(label) + PAD_X * 2;
    node.h = TEXT + PAD_Y * 2;
    node.rail = node.h / 2;
    node.label = label;
    return node;
  }

  // A group or the root. Each branch is a sequence; the branches stack.
  const branches = node.branches.map((branch) => measureSequence(branch.children));

  let inner;

  if (branches.length === 1) {
    inner = branches[0];
  } else {
    const width = Math.max(...branches.map((b) => b.w)) + FAN * 2;
    let height = 0;

    for (const branch of branches) {
      branch.offsetY = height;
      height += branch.h + BRANCH_GAP;
    }

    height -= BRANCH_GAP;

    inner = {
      branches,
      w: width,
      h: height,
      // **The rail enters at the FIRST branch**, not at the middle. A pattern
      // reads top to bottom, so the first alternative is the one the eye
      // follows straight through; centring it puts the through line in the gap
      // between two branches, which reads as neither.
      rail: branches[0].rail,
    };
  }

  node.inner = inner;
  node.branchLayouts = branches;

  if (node.kind === "root") {
    node.w = inner.w;
    node.h = inner.h;
    node.rail = inner.rail;
    return node;
  }

  // A group draws a frame around its contents, with its kind written above.
  //
  // **THE LABEL CAN BE WIDER THAN THE CONTENTS, and the frame is sized to the
  // wider of the two.** `(?<transaction_reference>x)` is labelled around a box
  // holding one item, and sizing the frame from the contents alone spills the
  // label across the next group's frame. The contents are then centred, so a
  // widened frame does not shunt its own track to one side.
  node.label = groupLabel(node);

  const labelWidth = node.label.length * LABEL_CHAR + 16;

  node.w = Math.max(inner.w + GROUP_PAD * 2, labelWidth);
  node.innerOffsetX = (node.w - inner.w) / 2;
  node.h = inner.h + GROUP_PAD * 2 + GROUP_LABEL;
  node.rail = GROUP_LABEL + GROUP_PAD + inner.rail;

  const quantifier = quantifierOf(node);

  if (quantifier) {
    // The loop hangs below the box, so it costs height and nothing else: the
    // rails on either side are unmoved.
    node.h += LOOP;
    node.quantifier = quantifier;
  }

  return node;
}

function measureSequence(children) {
  if (children.length === 0) {
    // An empty branch is a real thing - `(|a)` - and it is drawn as a short
    // length of rail rather than as nothing, or the alternation loses a row.
    return { children: [], w: GAP * 2, h: TEXT, rail: TEXT / 2, empty: true };
  }

  const measured = children.map(measure);

  let width = 0;
  let above = 0;
  let below = 0;

  for (const child of measured) {
    child.offsetX = width;
    width += child.w + GAP;
    above = Math.max(above, child.rail);
    below = Math.max(below, child.h - child.rail);
  }

  width -= GAP;

  for (const child of measured) {
    child.offsetY = above - child.rail;
  }

  return { children: measured, w: width, h: above + below, rail: above };
}

/* ---------------------------------------------------------------- labelling */

/** What a leaf says. Trailing inert source is trimmed so a box reads as the
 *  thing it is rather than as the thing plus the whitespace that followed it -
 *  which is what extended mode leaves behind on every item. */
function display(node) {
  if (node.kind === "inert") {
    return node.text.trim() || "(nothing)";
  }

  return node.text.replace(/\s+$/, "") || node.text;
}

/** A group's quantifier, which the enumeration puts on its CLOSING paren. */
function quantifierOf(node) {
  const quantifier = node.close.slice(1);
  return quantifier.length > 0 ? quantifier : null;
}

/** What to write above a group's frame. The kinds come from `classifyOpen`, so
 *  this is a translation into words a reader wants rather than a second
 *  classification. */
const GROUP_NAMES = {
  capture: "group",
  "non-capture": "group",
  // **"group year", not "named group year".** The owner's call, 2026-09-09: a
  // frame is sized to fit its own label, so the longest label is the one that
  // decides how wide a small group gets drawn - and "named" adds six characters
  // that tell the reader nothing they cannot see from the name being there.
  named: "group",
  atomic: "atomic",
  lookahead: "followed by",
  "negative-lookahead": "not followed by",
  lookbehind: "preceded by",
  "negative-lookbehind": "not preceded by",
  "branch-reset": "either",
  conditional: "if",
  "scoped-options": "options",
};

function groupLabel(node) {
  return groupNameFor(node.open) ?? GROUP_NAMES[node.groupKind] ?? node.groupKind;
}

/**
 * What to call a group, given only its opening syntax. Null if that text opens
 * no group.
 *
 * **Exported because the step flowchart labels the same constructs**, and two
 * vocabularies on one page is worse than either of them: a reader who sees
 * "followed by" in the diagram and "lookahead" in the flowchart has to work out
 * that they are the same thing before they can use either.
 */
export function groupNameFor(open) {
  const kind = classifyOpen(open);

  if (kind === null) {
    return null;
  }

  const name = GROUP_NAMES[kind] ?? kind;

  // A named group says its own name, which is the thing the reader is looking
  // for when they are reading somebody else's pattern.
  const named = /^\(\?P?[<']([A-Za-z_][A-Za-z0-9_]*)[>']$/.exec(open);

  return named ? `${name} ${named[1]}` : name;
}

/* ---------------------------------------------------------------- rendering */

/**
 * Lay the tree out and write it as SVG.
 *
 * Returns `{ svg, width, height }`.
 *
 * **IT IS DRAWN AT ACTUAL SIZE AND THE PANEL SCROLLS**, rather than scaled to
 * fit its container. The owner's call, 2026-09-09, and it is the right way
 * round: scaling to fit shrinks the diagram exactly as the pattern gets
 * complicated, so the picture becomes unreadable at the moment it is most
 * needed. A wide diagram scrolls sideways inside its own panel instead, which
 * costs the reader a drag and costs the page nothing.
 *
 * **Only the horizontal scroll is real.** A pattern grows sideways as it gets
 * longer; it grows downwards only with alternation, and the panel has no height
 * limit, so it simply gets taller.
 */
export function draw(root) {
  measure(root);

  const parts = [];
  const width = root.w + GAP * 2;
  const height = root.h + GAP * 2;

  // The rail comes in from the left edge and leaves at the right, so the
  // diagram reads as a length of track rather than as a floating box.
  const rail = GAP + root.rail;
  parts.push(line(0, rail, GAP, rail));
  parts.push(line(width - GAP, rail, width, rail));

  place(root, GAP, GAP, parts);

  // The width and height attributes are what give it an intrinsic size; the
  // viewBox stays so the coordinates inside are independent of them.
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(width)}" height="${round(height)}" ` +
    `viewBox="0 0 ${round(width)} ${round(height)}" ` +
    `class="rx-diagram" role="img" aria-label="A diagram of the regular expression">` +
    parts.join("") +
    `</svg>`;

  return { svg, width, height };
}

function place(node, x, y, parts) {
  if (node.kind === "atom" || node.kind === "inert") {
    const cls = node.kind === "inert" ? "rx-d-inert" : "rx-d-atom";
    parts.push(
      `<rect class="${cls}" x="${round(x)}" y="${round(y)}" width="${round(node.w)}" ` +
      `height="${round(node.h)}" rx="${round(node.h / 2)}"${span(node)}/>`);
    parts.push(
      `<text class="rx-d-text" x="${round(x + node.w / 2)}" y="${round(y + node.rail)}" ` +
      `text-anchor="middle" dominant-baseline="central">${escape(node.label)}</text>`);
    return;
  }

  if (node.kind === "root") {
    placeInner(node.inner, x, y, parts);
    return;
  }

  // The frame, its label, and then the contents inside it.
  const frameHeight = node.h - (node.quantifier ? LOOP : 0);

  parts.push(
    `<rect class="rx-d-group" x="${round(x)}" y="${round(y)}" width="${round(node.w)}" ` +
    `height="${round(frameHeight)}" rx="8"${span(node)}/>`);

  parts.push(
    `<text class="rx-d-label" x="${round(x + 8)}" y="${round(y + GROUP_LABEL - 2)}">` +
    `${escape(node.label)}</text>`);

  // **The rail has to cross the frame's padding on both sides.** Without these
  // the track stops at the group's edge and starts again inside it, and the
  // diagram reads as boxes floating near a line rather than as one route. Found
  // by rendering one and looking at it, which is the only way this kind of
  // defect is ever found.
  const railY = y + node.rail;
  parts.push(line(x, railY, x + node.innerOffsetX, railY));
  parts.push(line(x + node.innerOffsetX + node.inner.w, railY, x + node.w, railY));

  placeInner(node.inner, x + node.innerOffsetX, y + GROUP_LABEL + GROUP_PAD, parts);

  if (node.quantifier) {
    // A loop under the box, from the exit rail back to the entry rail, with the
    // quantifier written on it. This is the whole of what a quantifier means:
    // go round again.
    const bottom = y + frameHeight + LOOP - 4;
    const left = x - 4;
    const right = x + node.w + 4;

    parts.push(
      `<path class="rx-d-rail" d="M ${round(right)} ${round(railY)} ` +
      `Q ${round(right + 6)} ${round(railY)} ${round(right + 6)} ${round(railY + 8)} ` +
      `L ${round(right + 6)} ${round(bottom - 6)} ` +
      `Q ${round(right + 6)} ${round(bottom)} ${round(right)} ${round(bottom)} ` +
      `L ${round(left)} ${round(bottom)} ` +
      `Q ${round(left - 6)} ${round(bottom)} ${round(left - 6)} ${round(bottom - 6)} ` +
      `L ${round(left - 6)} ${round(railY + 8)} ` +
      `Q ${round(left - 6)} ${round(railY)} ${round(left)} ${round(railY)}"/>`);

    parts.push(
      `<text class="rx-d-quantifier" x="${round(x + node.w / 2)}" y="${round(bottom - 3)}" ` +
      `text-anchor="middle">${escape(node.quantifier)}</text>`);
  }
}

function placeInner(inner, x, y, parts) {
  if (inner.branches) {
    const railY = y + inner.rail;

    inner.branches.forEach((branch) => {
      const branchY = y + branch.offsetY;
      const branchRail = branchY + branch.rail;
      const left = x + FAN;
      const right = x + inner.w - FAN;

      // The fan: straight through for the first branch, a corner down to each
      // of the others. Drawn from the shared entry and to the shared exit, so
      // an alternation reads as one decision rather than as separate tracks.
      parts.push(fan(x, railY, left, branchRail));
      parts.push(fan(x + inner.w, railY, right, branchRail));

      placeSequence(branch, left, branchY, parts);

      // **A branch narrower than the widest one needs its rail extending to the
      // exit fan.** Branches are laid out from the same left edge and the
      // alternation is as wide as its widest, so every other branch stops short
      // - and the track visibly gives up somewhere in the middle of the box.
      // Found by rendering one and looking at it.
      if (left + branch.w < right) {
        parts.push(line(left + branch.w, branchRail, right, branchRail));
      }
    });

    return;
  }

  placeSequence(inner, x, y, parts);
}

function placeSequence(sequence, x, y, parts) {
  if (sequence.empty || sequence.children.length === 0) {
    parts.push(line(x, y + sequence.rail, x + sequence.w, y + sequence.rail));
    return;
  }

  const railY = y + sequence.rail;
  let previousRight = null;

  for (const child of sequence.children) {
    const childX = x + child.offsetX;

    if (previousRight !== null) {
      parts.push(line(previousRight, railY, childX, railY));
    }

    place(child, childX, y + child.offsetY, parts);
    previousRight = childX + child.w;
  }
}

/** A rail from a shared point to a branch, straight when they are level and
 *  cornered when they are not. */
function fan(fromX, fromY, toX, toY) {
  if (Math.abs(fromY - toY) < 0.5) {
    return line(fromX, fromY, toX, fromY);
  }

  const midX = fromX < toX ? fromX + RADIUS : fromX - RADIUS;
  const down = toY > fromY ? 1 : -1;
  const sweep = fromX < toX ? (down > 0 ? 1 : 0) : (down > 0 ? 0 : 1);

  return (
    `<path class="rx-d-rail" d="M ${round(fromX)} ${round(fromY)} ` +
    `A ${RADIUS} ${RADIUS} 0 0 ${sweep} ${round(midX)} ${round(fromY + RADIUS * down)} ` +
    `L ${round(midX)} ${round(toY - RADIUS * down)} ` +
    `A ${RADIUS} ${RADIUS} 0 0 ${1 - sweep} ${round(midX + (toX - midX ? Math.sign(toX - midX) * RADIUS : 0))} ${round(toY)} ` +
    `L ${round(toX)} ${round(toY)}"/>`
  );
}

/**
 * **Every drawn box carries the span of pattern text it stands for**, so the
 * step debugger can find the box for the item the engine is trying and light it
 * up. Added at step 7; the diagram existed for a page before anything needed to
 * point INTO it.
 *
 * The span is what `stackwalk.js` recorded from the enumeration, which is the
 * same coordinate the trace reports - so the two meet with no lookup table.
 */
const span = (node) =>
  node.span ? ` data-start="${node.span.start}" data-end="${node.span.end}"` : "";

const line = (x1, y1, x2, y2) =>
  `<line class="rx-d-rail" x1="${round(x1)}" y1="${round(y1)}" x2="${round(x2)}" y2="${round(y2)}"/>`;

/** Two decimal places. The numbers come out of arithmetic on floats, and an
 *  SVG full of `123.45000000000002` is both larger and harder to diff. */
const round = (value) => Math.round(value * 100) / 100;

const escape = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
